import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
} from "@mui/material";
import { Visibility, VisibilityOff, Email } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { inputStyles } from "../styles/login.styles";
import { useLoginForm } from "../hooks/useLoginForm";

export default function LoginForm({
  onSubmit,
  error: propError,
  loading: propLoading,
}) {
  const {
    email,
    password,
    showPassword,
    setShowPassword,
    localErrors,
    generalError,
    handleEmailChange,
    handlePasswordChange,
    handleSubmit,
  } = useLoginForm(onSubmit, propError, propLoading);

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography
        variant="h6"
        sx={{
          color: "white",
          fontWeight: 600,
          mb: 3,
          textAlign: "center",
        }}
      >
        Welcome Back
      </Typography>

      <TextField
        fullWidth
        label="Email Address"
        type="email"
        variant="filled"
        value={email}
        onChange={(e) => handleEmailChange(e.target.value)}
        error={!!localErrors.email}
        helperText={localErrors.email}
        sx={{ ...inputStyles, mb: 2 }}
        InputProps={{
          disableUnderline: true,
          startAdornment: (
            <InputAdornment position="start">
              <Email sx={{ color: "rgba(255,255,255,0.4)" }} />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        label="Password"
        type={showPassword ? "text" : "password"}
        variant="filled"
        value={password}
        onChange={(e) => handlePasswordChange(e.target.value)}
        error={!!localErrors.password}
        helperText={localErrors.password}
        sx={{ ...inputStyles, mb: 1 }}
        InputProps={{
          disableUnderline: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                sx={{ color: "rgba(255,255,255,0.4)" }}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 2,
        }}
      >
        <Button
          component={Link}
          to="/forgot-password"
          sx={{
            color: "#21cbf3",
            fontSize: "0.85rem",
            textTransform: "none",
            p: 0,
            minWidth: "auto",
            "&:hover": {
              background: "transparent",
              textDecoration: "underline",
            },
          }}
        >
          Forgot Password?
        </Button>
      </Box>

      {(generalError || propError) && (
        <Alert
          severity="error"
          variant="filled"
          sx={{
            mb: 2,
            borderRadius: "14px",
            bgcolor: "rgba(211, 47, 47, 0.8)",
          }}
        >
          {generalError || propError}
        </Alert>
      )}

      <Button
        type="submit"
        fullWidth
        size="large"
        disabled={propLoading}
        sx={{
          py: 1.7,
          borderRadius: "14px",
          fontWeight: 700,
          textTransform: "none",
          fontSize: "1rem",
          background: "linear-gradient(135deg, #2196f3 0%, #21cbf3 100%)",
          boxShadow: "0 8px 20px rgba(33,150,243,0.3)",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 12px 25px rgba(33,150,243,0.5)",
          },
          "&:disabled": {
            background: "rgba(33,150,243,0.6)",
          },
        }}
      >
        Sign In
      </Button>

      <Divider sx={{ my: 2.5, borderColor: "rgba(255,255,255,0.1)" }} />

      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.6)", mb: 0.5 }}
        >
          Don't have an account?
        </Typography>

        <Button
          component={Link}
          to="/signup"
          sx={{
            color: "#21cbf3",
            fontWeight: 200,
            textTransform: "none",
            fontSize: "0.95rem",
            p: 1,
            "&:hover": {
              bgcolor: "rgba(33,203,243,0.1)",
              borderRadius: "10px",
            },
          }}
        >
          Create Account
        </Button>
      </Box>
    </Box>
  );
}
